import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import { Icon24Add } from '@vkontakte/icons';

import Text from '@/components/Text';
import Button, { ButtonType } from '@/components/Button';
import AssetImage from '@/components/AssetImage';
import FeatureLinkCard from '@/components/FeatureLinkCard';
import cardIconLearn from '@/assets/card_icon_learn.png';
import { GeneratedCourseRecord } from '@/utils/supabase';

interface LearnDashboardProps {
  courses: GeneratedCourseRecord[];
  isLoading?: boolean;
  onGenerateNew: () => void;
  onSelectCourse: (course: GeneratedCourseRecord) => void;
  isAuthenticated?: boolean;
}

export default function LearnDashboard({
  courses,
  isLoading = false,
  onGenerateNew,
  onSelectCourse,
  isAuthenticated = false,
}: LearnDashboardProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className={`flex flex-col gap-8 pb-20 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <Head>
        <title>Learn | Bloom</title>
      </Head>

      <FeatureLinkCard
        onClick={onGenerateNew}
        title="📚 Learn Your Way"
        description="Get AI-generated courses on any stock or industry, personalized to your skill level."
        cta="Generate Course"
        iconSrc={cardIconLearn}
        horizontalDesktop={isAuthenticated}
      />

      {/* Courses Section */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between px-4 sm:px-0 gap-3">
          <div className="flex justify-between items-center gap-4 py-2 flex-1 min-w-0 !mb-0">
            <h2 className="flex items-center gap-2 font-sans font-normal text-base md:text-title-1">Your Courses</h2>
          </div>
          <Button 
            type={ButtonType.Primary}
            size="sm"
            onClick={onGenerateNew}
            className="w-auto md:!h-14 md:text-md whitespace-nowrap flex-shrink-0"
          >
            Generate Course
          </Button>
        </div>

        <div className="mt-2">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <CourseCardSkeleton key={i} />
              ))}
            </div>
          ) : courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {courses.map((course, index) => (
                <div 
                  key={course.id}
                  className="transition-all duration-500 ease-out"
                  style={{ 
                    transitionDelay: `${index * 100}ms`,
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
                  }}
                >
                <CourseCard 
                  course={course} 
                  onClick={() => onSelectCourse(course)} 
                />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 px-6 bg-darkglass rounded-2xl border border-white/5 text-center animate-in fade-in zoom-in duration-700">
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-6 transition-transform hover:rotate-12 duration-300">
                <Icon24Add width={32} height={32} className="text-secondary" />
              </div>
              <div className="flex justify-center items-center gap-4 py-2 w-full mb-2">
                <h3 className="flex items-center gap-2 font-sans font-normal text-title-2">No courses yet</h3>
              </div>
              <Text className="text-secondary max-w-xs mb-8 font-sans font-normal">
                Pick a stock or industry to generate your first AI-powered learning journey.
              </Text>
              <Button 
                type={ButtonType.Primary}
                size="md"
                onClick={onGenerateNew}
              >
                Start Learning
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course, onClick }: { course: GeneratedCourseRecord; onClick: () => void }) {
  const { course_payload, course_title } = course;
  const ticker = course_payload.metadata?.ticker || '';

  return (
    <div 
      onClick={onClick}
      className="group cursor-pointer bg-darkglass hover:bg-white/10 border border-white/5 hover:border-white/10 rounded-2xl p-5 transition-all duration-300 hover:shadow-xl hover:shadow-blue/5 hover:-translate-y-1 active:scale-[0.98]"
    >
      <div className="flex items-center justify-center gap-4">
        <div className="flex-shrink-0 transition-transform duration-500 group-hover:scale-110">
          <AssetImage 
            alt={ticker} 
            src="" // AssetImage handles fetching by symbol
            width={56} 
            height={56} 
            className="w-14 h-14 rounded-xl shadow-lg group-hover:shadow-blue/20 transition-shadow" 
          />
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-white/10 text-secondary text-[10px] font-bold uppercase px-1.5 py-0.5 rounded tracking-wider group-hover:bg-btn-primary group-hover:text-white transition-colors duration-300">
              {ticker}
            </span>
          </div>
          <h3 className="text-primary text-md leading-tight line-clamp-1 group-hover:text-btn-primary transition-colors duration-300 font-sans font-normal">
            {course_title || 'Untitled Course'}
          </h3>
        </div>
      </div>
    </div>
  );
}

function CourseCardSkeleton() {
  return (
    <div className="bg-darkglass border border-white/5 rounded-2xl p-5 animate-pulse">
      <div className="flex items-center justify-center gap-4">
        <div className="flex-shrink-0">
          <div className="w-14 h-14 bg-white/10 rounded-xl"></div>
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-4 w-12 bg-white/10 rounded"></div>
          </div>
          <div className="h-5 w-3/4 bg-white/10 rounded"></div>
        </div>
      </div>
    </div>
  );
}
